import { SlidersHorizontal, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/shadcn/button'
import { Label } from '@/components/ui/shadcn/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/shadcn/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/shadcn/tooltip'

export { type GenerationStyle } from '@backend/lib/ai/prompts/personaPrompt'
import type { GenerationStyle } from '@backend/lib/ai/prompts/personaPrompt'
import type { PresetAspectRatio } from '@backend/lib/util/aspectRatio'

// The design ratios the canvas generator accepts. 'auto' is a UI-only choice
// that lets the generator pick per topic (Stories/Reels → 9:16, feed → 1:1); an
// explicit ratio overrides that per-topic choice for every generated design.
export type DesignAspectRatio = PresetAspectRatio
export type DesignAspectRatioChoice = DesignAspectRatio | 'auto'

// A curated subset of the presets the generator can render. Fresh posts get the
// four common social formats; the rest of `PRESET_ASPECT_RATIOS` exists so an
// already-authored design keeps its shape when regenerated.
//
// `w`/`h` are the px dimensions of the little preview rectangle; 'auto' has none
// (it renders a sparkle instead).
const ASPECT_RATIO_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: '1:1', label: '1:1', w: 18, h: 18 },
  { value: '4:5', label: '4:5', w: 16, h: 20 },
  { value: '16:9', label: '16:9', w: 24, h: 14 },
  { value: '9:16', label: '9:16', w: 12, h: 22 },
] as const

interface Props {
  style: GenerationStyle
  onChange: (style: GenerationStyle) => void
  aspectRatio: DesignAspectRatioChoice
  onAspectRatioChange: (value: DesignAspectRatioChoice) => void
}

export function GenerationStylePopover({
  style,
  onChange,
  aspectRatio,
  onAspectRatioChange,
}: Props) {
  // 'auto' removes the key: the post then follows the brand brief and the
  // platform defaults instead of a value the user never picked.
  const update = <K extends keyof GenerationStyle>(key: K, value: string) => {
    const next = { ...style }
    if (value === 'auto') delete next[key]
    else next[key] = value as GenerationStyle[K]
    onChange(next)
  }

  return (
    <Popover>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon">
                <SlidersHorizontal className="text-foreground h-5 w-5" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <span>Generation style</span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <PopoverContent align="end" className="w-64 space-y-4 p-4">
        <div>
          <p className="text-foreground text-sm font-medium">Generation Style</p>
          <p className="text-muted-foreground mt-0.5 text-xs">
            Auto follows your moodboard&apos;s voice.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label className="text-muted-foreground text-xs">Post Length</Label>
          <Select value={style.postLength ?? 'auto'} onValueChange={(v) => update('postLength', v)}>
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto</SelectItem>
              <SelectItem value="short">Short</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="long">Long</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-muted-foreground text-xs">Emoji Usage</Label>
          <Select value={style.emojiUsage ?? 'auto'} onValueChange={(v) => update('emojiUsage', v)}>
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto</SelectItem>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="minimal">Minimal</SelectItem>
              <SelectItem value="moderate">Moderate</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-muted-foreground text-xs">Hashtags</Label>
          <Select
            value={style.hashtagUsage ?? 'auto'}
            onValueChange={(v) => update('hashtagUsage', v)}>
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto</SelectItem>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="few">Few</SelectItem>
              <SelectItem value="standard">Standard</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-muted-foreground text-xs">Aspect ratio</Label>
          <div className="grid grid-cols-5 gap-1.5">
            {ASPECT_RATIO_OPTIONS.map((opt) => {
              const active = aspectRatio === opt.value
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onAspectRatioChange(opt.value)}
                  className={cn(
                    'group flex h-12 flex-col items-center justify-center gap-1 rounded-md border transition-colors',
                    active ? 'border-primary bg-primary/5' : 'border-border hover:bg-hover',
                  )}>
                  {opt.value === 'auto' ? (
                    <Sparkles
                      size={14}
                      className={cn(
                        'transition-colors',
                        active
                          ? 'text-primary'
                          : 'text-muted-foreground group-hover:text-foreground/70',
                      )}
                    />
                  ) : (
                    <span
                      className={cn(
                        'block rounded-[2px] transition-colors',
                        active
                          ? 'bg-primary'
                          : 'bg-muted-foreground/40 group-hover:bg-muted-foreground',
                      )}
                      style={{ width: opt.w, height: opt.h }}
                    />
                  )}
                  <span
                    className={cn(
                      'text-[9px] tabular-nums',
                      active ? 'text-primary' : 'text-muted-foreground',
                    )}>
                    {opt.label}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
